import { useWorkOrderAlerts } from '@/features/work-orders/hooks/useWorkOrderAlerts.tsx';
import {
  getAlertsControllerGetAlertsQueryKey,
  useAlertsControllerAcknowledge,
  useAlertsControllerResolve,
} from '@/generated/api/alerts.ts';
import type { AlertDto } from '@/generated/api/pacApi.schemas.ts';
import {
  Badge,
  Box,
  Button,
  Divider,
  Group,
  Paper,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  CaretDownIcon,
  CaretUpIcon,
  CheckCircleIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useMemo, useState } from 'react';

export default function WorkOrderAlerts({
  workOrderId,
}: {
  workOrderId?: string;
}) {
  const queryClient = useQueryClient();
  const alerts = useWorkOrderAlerts(workOrderId);

  const actionableAlerts = useMemo(
    () =>
      (alerts.data?.results ?? [])
        .filter(
          (alert) => alert.severity === 'medium' || alert.severity === 'high',
        )
        .sort(compareAlerts),
    [alerts.data],
  );

  const refreshAlerts = () =>
    queryClient.invalidateQueries({
      queryKey: getAlertsControllerGetAlertsQueryKey(),
    });

  const acknowledge = useAlertsControllerAcknowledge({
    mutation: {
      onSuccess: () => {
        notifications.show({ message: 'Alert acknowledged', color: 'green' });
        void refreshAlerts();
      },
      onError: () => {
        notifications.show({
          message: 'Failed to acknowledge alert',
          color: 'red',
        });
      },
    },
  });

  const resolve = useAlertsControllerResolve({
    mutation: {
      onSuccess: () => {
        notifications.show({ message: 'Alert resolved', color: 'green' });
        void refreshAlerts();
      },
      onError: () => {
        notifications.show({
          message: 'Failed to resolve alert',
          color: 'red',
        });
      },
    },
  });

  if (!workOrderId || alerts.isLoading || (alerts.isError && !alerts.data))
    return null;
  if (actionableAlerts.length === 0) return null;

  const busy = acknowledge.isPending || resolve.isPending;

  return (
    <Stack gap="xs" aria-label="Work order alerts" aria-live="polite">
      {(['high', 'medium'] as const).map((severity) => {
        const severityAlerts = actionableAlerts.filter(
          (alert) => alert.severity === severity,
        );
        if (severityAlerts.length === 0) return null;

        return (
          <AlertGroup
            key={severity}
            severity={severity}
            alerts={severityAlerts}
            busy={busy}
            acknowledgingId={
              acknowledge.isPending ? acknowledge.variables?.id : undefined
            }
            resolvingId={resolve.isPending ? resolve.variables?.id : undefined}
            onAcknowledge={(id) => acknowledge.mutate({ id })}
            onResolve={(id) => resolve.mutate({ id })}
          />
        );
      })}
    </Stack>
  );
}

function AlertGroup({
  severity,
  alerts,
  busy,
  acknowledgingId,
  resolvingId,
  onAcknowledge,
  onResolve,
}: {
  severity: 'medium' | 'high';
  alerts: AlertDto[];
  busy: boolean;
  acknowledgingId?: string;
  resolvingId?: string;
  onAcknowledge: (id: string) => void;
  onResolve: (id: string) => void;
}) {
  const [opened, setOpened] = useState(true);
  const label = severity === 'high' ? 'High priority' : 'Medium priority';

  return (
    <Paper withBorder radius="md" style={{ overflow: 'hidden' }}>
      <UnstyledButton
        w="100%"
        aria-expanded={opened}
        aria-label={`${opened ? 'Collapse' : 'Expand'} ${label.toLowerCase()} alerts`}
        onClick={() => setOpened((value) => !value)}
      >
        <Group justify="space-between" gap="sm" px="sm" py="xs">
          <Group gap="xs" wrap="nowrap">
            <WarningCircleIcon
              aria-hidden="true"
              size={18}
              weight="fill"
              color={`var(--mantine-color-${severityColor(severity)}-6)`}
            />
            <Text size="sm" fw={700}>
              {label}
            </Text>
            <Badge size="sm" color={severityColor(severity)} variant="light">
              {alerts.length}
            </Badge>
          </Group>
          {opened ? (
            <CaretUpIcon aria-hidden="true" size={16} />
          ) : (
            <CaretDownIcon aria-hidden="true" size={16} />
          )}
        </Group>
      </UnstyledButton>

      {opened ? (
        <>
          <Divider />
          <Stack gap={0}>
            {alerts.map((alert, index) => (
              <Box key={alert.id}>
                {index > 0 ? <Divider /> : null}
                <AlertRow
                  alert={alert}
                  busy={busy}
                  acknowledging={acknowledgingId === alert.id}
                  resolving={resolvingId === alert.id}
                  onAcknowledge={() => onAcknowledge(alert.id)}
                  onResolve={() => onResolve(alert.id)}
                />
              </Box>
            ))}
          </Stack>
        </>
      ) : null}
    </Paper>
  );
}

function AlertRow({
  alert,
  busy,
  acknowledging,
  resolving,
  onAcknowledge,
  onResolve,
}: {
  alert: AlertDto;
  busy: boolean;
  acknowledging: boolean;
  resolving: boolean;
  onAcknowledge: () => void;
  onResolve: () => void;
}) {
  const acknowledged = alert.state === 'acknowledged';
  const firedAt = DateTime.fromISO(alert.firedAt).toRelative();
  const acknowledgedAt = alert.acknowledgedAt
    ? DateTime.fromISO(alert.acknowledgedAt).toRelative()
    : null;

  return (
    <Stack gap={6} px="sm" py="sm">
      <Group justify="space-between" align="flex-start" gap="sm" wrap="nowrap">
        <Stack gap={2} style={{ minWidth: 0 }}>
          <Text size="sm" fw={700} lineClamp={1}>
            {alert.title}
          </Text>
        </Stack>
        {firedAt ? (
          <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
            {firedAt}
          </Text>
        ) : null}
      </Group>

      <Text size="sm">{alert.message}</Text>

      {acknowledged && alert.acknowledgedBy ? (
        <Group gap={4} align="center">
          <CheckCircleIcon size={12} color="green" />
          <Text size="xs" c="dimmed">
            Acknowledged by {alert.acknowledgedBy.name} - {acknowledgedAt}
          </Text>
        </Group>
      ) : null}

      <Group gap="xs" justify="flex-end">
        {!acknowledged ? (
          <Button
            size="compact-sm"
            variant="default"
            disabled={busy}
            loading={acknowledging}
            onClick={onAcknowledge}
          >
            Acknowledge
          </Button>
        ) : null}
        {alert.manualResolutionAllowed ? (
          <Button
            size="compact-sm"
            variant="subtle"
            color="gray"
            disabled={busy}
            loading={resolving}
            onClick={onResolve}
          >
            Resolve
          </Button>
        ) : null}
      </Group>
    </Stack>
  );
}

function compareAlerts(left: AlertDto, right: AlertDto): number {
  const severityDifference =
    severityRank(right.severity) - severityRank(left.severity);
  if (severityDifference !== 0) return severityDifference;
  return new Date(left.firedAt).getTime() - new Date(right.firedAt).getTime();
}

function severityRank(severity: AlertDto['severity']): number {
  if (severity === 'high') return 2;
  if (severity === 'medium') return 1;
  return 0;
}

function severityColor(severity: AlertDto['severity']): 'orange' | 'red' {
  return severity === 'high' ? 'red' : 'orange';
}
